import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Trash2, Send, X } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { DependsOnPicker } from './DependsOnPicker';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const errMsg = (e, d) => e?.response?.data?.detail || d;

// task=null => create mode
export const TaskDialog = ({ open, onClose, task, tasks, stages, defaultStage, projectId, onSaved }) => {
  const [form, setForm] = useState({ title: '', stage: '', description: '', depends_on: [] });
  const [note, setNote] = useState('');
  const [current, setCurrent] = useState(task);

  useEffect(() => {
    setCurrent(task);
    setNote('');
    setForm(task
      ? { title: task.title, stage: task.stage, description: task.description || '', depends_on: task.depends_on || [] }
      : { title: '', stage: defaultStage || stages[0] || '', description: '', depends_on: [] });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [task, open, defaultStage]);

  const options = tasks.filter(t => t.task_id !== task?.task_id).map(t => ({ id: t.task_id, label: t.title, group: t.stage }));

  const save = async () => {
    if (!form.title.trim() || !form.stage.trim()) return toast.error('Title and stage are required');
    try {
      if (task) await axios.put(`${BACKEND_URL}/api/tasks/${task.task_id}`, form, { withCredentials: true });
      else await axios.post(`${BACKEND_URL}/api/projects/${projectId}/tasks`, form, { withCredentials: true });
      toast.success(task ? 'Task updated' : 'Task added');
      onSaved(); onClose();
    } catch (e) { toast.error(errMsg(e, 'Failed to save task')); }
  };

  const remove = async () => {
    if (!window.confirm('Delete this task?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/tasks/${task.task_id}`, { withCredentials: true });
      toast.success('Task deleted'); onSaved(); onClose();
    } catch (e) { toast.error(errMsg(e, 'Failed to delete task')); }
  };

  const noteCall = async (method, url, body) => {
    try {
      const r = await axios[method](url, body, { withCredentials: true });
      setCurrent(r.data); setNote(''); onSaved();
    } catch (e) { toast.error(errMsg(e, 'Failed to update note')); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white max-w-xl max-h-[90vh] overflow-y-auto" data-testid="task-dialog">
        <DialogHeader><DialogTitle>{task ? 'Task details' : 'Add task'}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Task</Label>
            <Input data-testid="task-title-input" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} />
          </div>
          <div>
            <Label>Stage</Label>
            <Input data-testid="task-stage-input" list="task-stage-options" value={form.stage}
              onChange={e => setForm({ ...form, stage: e.target.value })} placeholder="Pick a stage or type a new one" />
            <datalist id="task-stage-options">{stages.map(s => <option key={s} value={s} />)}</datalist>
          </div>
          <div>
            <Label>Description</Label>
            <Textarea data-testid="task-description-input" rows={2} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} />
          </div>
          <div className="space-y-2">
            <Label className="block">Must finish first</Label>
            <DependsOnPicker options={options} value={form.depends_on} onChange={v => setForm({ ...form, depends_on: v })} testId="task-depends-picker" />
            <div className="flex flex-wrap gap-1.5">
              {form.depends_on.map(id => {
                const t = tasks.find(x => x.task_id === id);
                return t && (
                  <span key={id} className="inline-flex items-center gap-1 text-[11px] bg-amber-50 text-amber-900 border border-amber-200 rounded-full px-2 py-0.5">
                    {t.title}
                    <button type="button" onClick={() => setForm({ ...form, depends_on: form.depends_on.filter(d => d !== id) })}><X className="w-3 h-3" /></button>
                  </span>
                );
              })}
            </div>
          </div>
          <div className="flex gap-2">
            <Button onClick={save} data-testid="task-save-btn" className="flex-1 bg-amber-700 hover:bg-amber-800 text-white">{task ? 'Save changes' : 'Add task'}</Button>
            {task && <Button variant="outline" onClick={remove} data-testid="task-delete-btn" className="text-red-700 border-red-200 hover:bg-red-50"><Trash2 className="w-4 h-4" /></Button>}
          </div>

          {current && (
            <div className="border-t border-stone-200 pt-4">
              <p className="text-xs uppercase tracking-wider font-medium text-stone-500 mb-2">Notes & comments</p>
              <div className="space-y-2 mb-3" data-testid="task-notes-list">
                {(current.notes || []).length === 0 && <p className="text-xs text-stone-400">No notes yet.</p>}
                {(current.notes || []).map(n => (
                  <div key={n.note_id} className="group bg-stone-50 rounded-md px-3 py-2" data-testid={`task-note-${n.note_id}`}>
                    <div className="flex justify-between items-start gap-2">
                      <p className="text-sm text-stone-800 whitespace-pre-wrap">{n.text}</p>
                      <button onClick={() => noteCall('delete', `${BACKEND_URL}/api/tasks/${current.task_id}/notes/${n.note_id}`)}
                        data-testid={`task-note-delete-${n.note_id}`} className="md:opacity-0 md:group-hover:opacity-100 text-stone-400 hover:text-red-700 transition-opacity">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                    <p className="text-[10px] text-stone-400 mt-1">{n.author} · {new Date(n.created_at).toLocaleString('en-IN')}</p>
                  </div>
                ))}
              </div>
              <div className="flex gap-2">
                <Input data-testid="task-note-input" value={note} onChange={e => setNote(e.target.value)} placeholder="Add a note…"
                  onKeyDown={e => e.key === 'Enter' && note.trim() && noteCall('post', `${BACKEND_URL}/api/tasks/${current.task_id}/notes`, { text: note })} />
                <Button data-testid="task-note-add-btn" disabled={!note.trim()} onClick={() => noteCall('post', `${BACKEND_URL}/api/tasks/${current.task_id}/notes`, { text: note })}
                  className="bg-stone-900 hover:bg-stone-800 text-white"><Send className="w-4 h-4" /></Button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
